import type { OutreachMessage, OutreachRow, OutreachRowStatus, OutreachState } from '@discovr/types';

export type ReplyBlocker =
  | 'closed_unsubscribed'
  | 'closed_bounced'
  | 'closed_removed'
  | 'no_contact'
  | 'suppressed'
  | 'not_contacted'
  | 'first_mail_held'
  | 'past_outreach';

// After the 2.9 hand-off the roster status is the brand's. These still reply, mirroring Go's isConversationStatus;
// every rejected creator keeps the box for a polite close (owner ruling 2026-10-09).
const REPLYABLE_HANDED_OFF: ReadonlySet<OutreachRowStatus> = new Set<OutreachRowStatus>([
  'pending',
  'accepted_by_brand',
  'rejected_by_brand',
  'rejected_by_admin',
  'rejected_by_creator',
]);

const REJECTED_STATUSES: ReadonlySet<OutreachRowStatus> = new Set<OutreachRowStatus>(['rejected_by_brand', 'rejected_by_admin', 'rejected_by_creator']);

// bounced and unsubscribed are the one place "every thread is openable" is qualified: the send gate hard-refuses
// them, so the thread opens read-only. Every other stopped state leaves the composer to Go's own gate.
const SUPPRESSION_BLOCKER: Partial<Record<OutreachState, ReplyBlocker>> = {
  unsubscribed: 'closed_unsubscribed',
  bounced: 'closed_bounced',
};

export function replyBlocker(
  row: Pick<OutreachRow, 'status' | 'outreachState' | 'hasContact' | 'emailOverride' | 'suppressed' | 'lastMessage'>,
  messages: readonly Pick<OutreachMessage, 'direction' | 'kind' | 'status'>[] = [],
): ReplyBlocker | null {
  // A rejected row decides on its roster status alone, like Go's isConversationStatus; its stored outreach_state is
  // whatever it was at hand-off.
  const byStatusAlone = REJECTED_STATUSES.has(row.status);
  const suppression = byStatusAlone ? undefined : SUPPRESSION_BLOCKER[row.outreachState];
  if (suppression) return suppression;
  // ThreadView already renders its own role="status" banner for a removed creator ("Reply from a removed creator"),
  // so the composer stays quiet here rather than doubling it up with a second landmark carrying near-identical copy.
  // This is the one removed model: the roster's removed_by_admin and the thread's removal both arrive as state `removed`.
  if (!byStatusAlone && row.outreachState === 'removed') return 'closed_removed';
  // A roster status past outreach that is not one of those above has no thread to reply in regardless of contact
  // info, so it takes precedence over 'no_contact' — adding an email wouldn't unblock it.
  if (row.status !== 'outreach' && !REPLYABLE_HANDED_OFF.has(row.status)) return 'past_outreach';
  // lastMessage (an outbound message already went out) proves a working address is on file in Postgres even if the
  // entry's current contact (override/profile) has since gone empty — Go's QueueReply falls back to that same
  // thread address, so the composer must not block here either.
  if (!row.hasContact && !row.emailOverride && !row.lastMessage) return 'no_contact';
  if (row.suppressed) return 'suppressed';
  if (!byStatusAlone && row.outreachState === 'sourced') return 'not_contacted';
  if (firstMailHeldOnly(messages)) return 'first_mail_held';
  return null;
}

/** E9-164: the thread's only mail is a held first touch, nothing sent and nothing received. A hand-written first mail
 *  goes only through the wizard, so the box waits until the held one is sent or stopped. */
function firstMailHeldOnly(messages: readonly Pick<OutreachMessage, 'direction' | 'kind' | 'status'>[]): boolean {
  const out = messages.filter((m) => m.direction === 'out');
  return out.some((m) => m.kind === 'initial' && m.status === 'held') && !out.some((m) => m.status === 'sent') && !messages.some((m) => m.direction === 'in');
}

export const REPLY_BLOCKER_COPY: Record<ReplyBlocker, string> = {
  closed_unsubscribed: 'They unsubscribed, so replies are off for this thread.',
  closed_bounced: 'Their address bounced, so replies are off for this thread.',
  closed_removed: 'This creator was removed from the campaign, so replies are off for this thread.',
  no_contact: 'No email address for this creator yet. Add one to reply.',
  suppressed: 'This address is on the suppression list, so nothing can be sent to it.',
  not_contacted: 'Send the first message before replying.',
  first_mail_held: 'Send or stop the held first mail first.',
  past_outreach: 'This creator has moved past outreach. Continue on the campaign roster.',
};
